import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Icon from '../components/Icon';
import './PageEvenement.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─── Helpers ──────────────────────────────────

function fmtDateLong(d) {
  if (!d) return '';
  const date = new Date(d + 'T12:00:00');
  return date.toLocaleDateString('fr-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function fmt$(v) {
  return `${parseFloat(v).toFixed(0)} $`;
}

// ─── Countdown local ───────────────────────────

function useCountdown(dateStr) {
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    if (!dateStr) return;
    function calculer() {
      const cible = new Date(dateStr + 'T12:00:00').getTime();
      const now   = Date.now();
      const delta = cible - now;
      if (delta <= 0) { setDiff(0); return; }
      setDiff(delta);
    }
    calculer();
    const t = setInterval(calculer, 1000);
    return () => clearInterval(t);
  }, [dateStr]);

  if (diff === null) return null;
  if (diff === 0)    return { passe: true };

  const totalSec = Math.floor(diff / 1000);
  const jours    = Math.floor(totalSec / 86400);
  const heures   = Math.floor((totalSec % 86400) / 3600);
  const minutes  = Math.floor((totalSec % 3600) / 60);
  const secondes = totalSec % 60;
  return { passe: false, jours, heures, minutes, secondes };
}

function Countdown({ dateStr }) {
  const cd = useCountdown(dateStr);
  if (!cd) return null;

  const today = new Date().toISOString().slice(0, 10) === dateStr;
  if (today) {
    return (
      <div className="ev-countdown-banner ev-countdown-today">
        C'est aujourd'hui !
      </div>
    );
  }
  if (cd.passe) return null;

  return (
    <div className="ev-countdown-banner">
      <div className="countdown-label">L'événement commence dans</div>
      <div className="countdown-units">
        <div className="countdown-unit">
          <span className="countdown-val">{cd.jours}</span>
          <span className="countdown-lbl">jours</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-val">{String(cd.heures).padStart(2, '0')}</span>
          <span className="countdown-lbl">heures</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-val">{String(cd.minutes).padStart(2, '0')}</span>
          <span className="countdown-lbl">min</span>
        </div>
        <span className="countdown-sep">:</span>
        <div className="countdown-unit">
          <span className="countdown-val">{String(cd.secondes).padStart(2, '0')}</span>
          <span className="countdown-lbl">sec</span>
        </div>
      </div>
    </div>
  );
}

// ─── Card plateau ─────────────────────────────

function CardPlateauPreview({ plateau, onCommander, sidesEvenement = [] }) {
  const sidesRaw = plateau.sides_inclus || [];
  const sides    = sidesRaw.length > 0 ? sidesRaw : sidesEvenement;
  const contenu  = plateau.contenu || [];

  return (
    <div className="ev-plateau-card">
      <div className="ev-plateau-header">
        <div className="ev-plateau-label">{plateau.label}</div>
        <div className="ev-plateau-prix">{fmt$(plateau.prix)}</div>
      </div>

      {contenu.length > 0 && (
        <ul className="ev-plateau-contenu">
          {contenu.map((c, i) => (
            <li key={i} className="ev-plateau-contenu-item">
              <span className="ev-contenu-qty">{c.label || `${c.quantite}${c.unite} ${c.produit}`}</span>
            </li>
          ))}
        </ul>
      )}

      {sides.length > 0 && (
        <div className="ev-plateau-sides">
          <span className="muted ev-sides-lbl">+ {sides.join(' · ')}</span>
        </div>
      )}

      <button
        className="btn btn-primary ev-plateau-btn"
        onClick={() => onCommander(plateau.id)}
      >
        Commander
      </button>
    </div>
  );
}

// ─── Badge disponibilité ──────────────────────

function BadgeDisponibilite({ complet }) {
  return (
    <div className="ev-disponibilite">
      {complet ? (
        <span className="badge-complet">Complet</span>
      ) : (
        <span className="badge-disponible">Places disponibles</span>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────

export default function PageEvenement() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const location   = useLocation();
  const [ev, setEv]         = useState(null);
  const [chargement, setCh] = useState(true);
  const [erreur, setErreur] = useState('');
  const searchParams   = new URLSearchParams(location.search);
  const isPreview      = searchParams.get('preview') === '1';
  const previewToken   = searchParams.get('preview_token') || '';

  const charger = useCallback(async () => {
    setCh(true);
    setErreur('');
    try {
      const authToken = localStorage.getItem('escamel_token');
      const res = await axios.get(`${API}/api/public/evenements/${slug}`, {
        params: isPreview ? { preview: '1', preview_token: previewToken } : {},
        headers: isPreview && authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      setEv(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setErreur("Cet événement n'existe pas ou n'est plus disponible.");
      } else {
        setErreur('Impossible de charger cet événement. Réessayez plus tard.');
      }
    } finally {
      setCh(false);
    }
  }, [isPreview, previewToken, slug]);

  useEffect(() => { charger(); }, [charger]);

  function commander(plateauPreselectionne) {
    const previewQuery = isPreview
      ? `?preview=1${previewToken ? `&preview_token=${encodeURIComponent(previewToken)}` : ''}`
      : '';
    const path = `/precommande/${slug}${previewQuery}`;
    if (plateauPreselectionne) {
      navigate(path, { state: { plateauPreselectionne } });
    } else {
      navigate(path);
    }
  }

  // ─── États ────────────────────────────────
  if (chargement) {
    return (
      <div className="ev-loading">
        <div className="spinner-grand" />
        <div className="muted" style={{ fontStyle: 'italic' }}>Chargement…</div>
      </div>
    );
  }

  if (erreur) {
    return (
      <div className="ev-erreur">
        <div className="ev-logo">ESCAMEL</div>
        <div className="ev-erreur-msg">{erreur}</div>
      </div>
    );
  }

  const photoUrl  = ev.photo_hero_url || ev.photo_url || null;
  const logoUrl   = ev.logo_url
    ? (ev.logo_url.startsWith('/') ? `${API}${ev.logo_url}` : ev.logo_url)
    : null;
  const precommandeOuverte = Boolean(ev.precommande_ouverte || isPreview || ev.preview_mode);
  const mapsUrl = ev.google_maps_url
    || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [ev.adresse, ev.ville, 'Québec Canada'].filter(Boolean).join(', ')
      )}`;
  const complet = ev.statut === 'complet';

  return (
    <div className="ev-page">

      {/* ─── HERO ─────────────────────────────── */}
      <section
        className="ev-hero"
        style={photoUrl ? { backgroundImage: `url(${photoUrl})` } : {}}
      >
        <div className="ev-hero-overlay" />
        <div className="ev-hero-content">

          {logoUrl ? (
            <img src={logoUrl} alt="Escamel" className="ev-hero-logo-img" />
          ) : (
            <div className="ev-hero-logo">ESCAMEL</div>
          )}

          <div className="ev-hero-sep" />

          <h1 className="ev-hero-titre">{ev.nom}</h1>

          <div className="ev-hero-meta">
            {fmtDateLong(ev.date_event)}
          </div>

          {ev.lieu && (
            <div className="ev-hero-lieu">
              <Icon name="lieu" size={13} weight="fill" color="currentColor" />
              {ev.lieu}{ev.ville ? `, ${ev.ville}` : ''}
            </div>
          )}

          <Countdown dateStr={ev.date_event} />

          {!complet && precommandeOuverte && (
            <button
              className="btn btn-primary ev-hero-cta"
              onClick={() => commander(null)}
            >
              Réserver ma place
            </button>
          )}
          {complet && (
            <div className="ev-hero-complet">Événement complet</div>
          )}

          <a href="/commande" className="ev-hero-retrouver">
            <Icon name="recherche" size={13} color="currentColor" />
            Retrouver ma commande
          </a>

        </div>
      </section>

      {/* ─── INFOS ────────────────────────────── */}
      <section className="ev-infos">
        <div className="ev-infos-inner">
          <div className="ev-infos-grille">
            {/* Où */}
            <div className="ev-info-bloc">
              <div className="ev-info-icone">
                <Icon name="lieu" size={22} weight="duotone" />
              </div>
              <div>
                <div className="ev-info-titre">Où ?</div>
                <div className="ev-info-val">
                  {ev.lieu && <div>{ev.lieu}</div>}
                  {ev.adresse && <div className="muted">{ev.adresse}</div>}
                  {ev.ville && <div className="muted">{ev.ville}</div>}
                </div>
                {(ev.google_maps_embed || ev.adresse) && (
                  <div className="ev-maps-embed-wrap">
                    <iframe
                      title="carte"
                      className="ev-maps-iframe"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={ev.google_maps_embed || `https://maps.google.com/maps?q=${encodeURIComponent(
                        [ev.adresse, ev.ville, 'Québec Canada'].filter(Boolean).join(', ')
                      )}&output=embed`}
                    />
                  </div>
                )}
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="ev-lien-maps">
                  <Icon name="lien" size={13} />
                  Ouvrir dans Google Maps
                </a>
              </div>
            </div>

            {/* Quand */}
            <div className="ev-info-bloc">
              <div className="ev-info-icone">
                <Icon name="heure" size={22} weight="duotone" />
              </div>
              <div>
                <div className="ev-info-titre">Quand ?</div>
                <div className="ev-info-val">
                  <div>{fmtDateLong(ev.date_event)}</div>
                  {ev.heure_debut && ev.heure_fin && (
                    <div className="muted">{ev.heure_debut} à {ev.heure_fin}</div>
                  )}
                  {ev.slot_duree_min && (
                    <div className="muted">Pickup par créneaux de {ev.slot_duree_min} minutes</div>
                  )}
                </div>
              </div>
            </div>

            {/* Menu */}
            <div className="ev-info-bloc">
              <div className="ev-info-icone">
                <Icon name="repas" size={22} weight="duotone" />
              </div>
              <div>
                <div className="ev-info-titre">Qu'est-ce qu'on sert ?</div>
                <div className="ev-info-val">
                  {ev.description
                    ? <div>{ev.description}</div>
                    : <div>BBQ texan authentique fumé au bois de cerisier du Québec.<br />100&nbsp;% naturel, sans MSG ni nitrite.</div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PLATEAUX ─────────────────────────── */}
      {ev.plateaux && ev.plateaux.length > 0 && (
        <section className="ev-section ev-plateaux-section">
          <h2 className="ev-section-titre">Nos plateaux</h2>
          <div className="ev-plateaux-grille">
            {ev.plateaux.map(p => (
              <CardPlateauPreview
                key={p.id}
                plateau={p}
                onCommander={commander}
                sidesEvenement={
                  (ev.sides || [])
                    .filter(s => s.actif !== 0)
                    .sort((a, b) => a.ordre - b.ordre)
                    .map(s => `${s.emoji} ${s.nom}`)
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* ─── DISPONIBILITÉ + CTA bas ──────────── */}
      <section className="ev-section" style={{ textAlign: 'center' }}>
        <BadgeDisponibilite complet={complet} />
        {!complet && precommandeOuverte && (
          <div style={{ marginTop: 28 }}>
            <button className="btn btn-primary ev-cta-bas" onClick={() => commander(null)}>
              Réserver ma place
            </button>
          </div>
        )}
      </section>

      {/* ─── FOOTER ───────────────────────────── */}
      <footer className="ev-footer">
        <div className="ev-footer-logo">ESCAMEL</div>
        <div className="ev-footer-sub">Artisans du Fumoir</div>
        <a href="mailto:commandes@escamel.ca" className="ev-footer-contact">
          <Icon name="email" size={14} />
          commandes@escamel.ca
        </a>
      </footer>
    </div>
  );
}
